import { Injectable } from '@angular/core';
import { renderAsync } from 'docx-preview';
import Docxtemplater from 'docxtemplater';
import html2pdf from 'html2pdf.js';
import * as JSZipUtils from 'jszip-utils';
import PizZip from 'pizzip';

@Injectable({ providedIn: 'root' })
export class DeclarationPdfService {
  async fillAndDownloadPdf(lot: any, deliveryAddress: any, user: any) {
    const path = 'assets/documents/declaracao-conteudo.docx';
    const sender = user?.userLoginInfo ?? {};

    const waitImages = async (root: HTMLElement) => {
      const imgs = Array.from(root.querySelectorAll('img'));
      await Promise.all(
        imgs.map(
          (img) =>
            img.complete
              ? Promise.resolve()
              : new Promise<void>((res) => {
                img.onload = () => res();
                img.onerror = () => res();
              })
        )
      );
    };

    try {
      const content: ArrayBuffer = await new Promise((resolve, reject) => {
        JSZipUtils.getBinaryContent(path, (err, data) => (err ? reject(err) : resolve(data)));
      });

      const zip = new PizZip(content);
      const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true,
        delimiters: { start: '{{', end: '}}' },
      });

      const bottles = Array.isArray(lot?.bottles) ? lot.bottles : [];
      const lotIdentifier = lot?.lotIdentifier ?? lot?.lotId ?? '';
      const total = Number(lot?.amount) || 0;
      const count = bottles.length || 1;
      const perBottle = total / count;
      const rowQtyTotal = count;

      const rows: Record<string, any> = {};
      for (let i = 0; i < 6; i++) {
        const b = bottles[i];
        const idx = i + 1;
        rows[`row${idx}LotIdentifier`] = b ? String(lotIdentifier) : '';
        rows[`row${idx}ItemName`] = b ? `${b?.name ?? ''}${b?.year ? ` - ${b.year}` : ''}`.trim() : '';
        rows[`row${idx}Qty`] = b ? 1 : '';
        rows[`row${idx}Value`] = b ? `R$ ${perBottle.toFixed(2).replace('.', ',')}` : '';
      }

      const data = {
        // remetente
        senderName: sender?.name ?? '',
        senderAddress: `${sender?.street ?? ''}, ${sender?.streetNumber ?? ''} ${sender?.complement ?? ''}`.trim(),
        senderCity: sender?.city ?? '',
        senderState: sender?.state ?? '',
        senderZip: sender?.zipCode ?? '',
        senderDoc: lot?.sellerDocument ?? '',

        // destinatário
        recipientName: deliveryAddress?.recipient ?? '',
        recipientAddress: `${deliveryAddress?.street ?? ''}, ${deliveryAddress?.streetNumber ?? ''} - ${deliveryAddress?.neighborhood ?? ''}`.trim(),
        recipientCity: deliveryAddress?.city ?? '',
        recipientUF: deliveryAddress?.uf ?? '',
        recipientZip: deliveryAddress?.zipCode ?? '',
        recipientDoc: deliveryAddress?.document ?? '-',

        totalValue: `R$ ${total.toFixed(2).replace('.', ',')}`,
        rowQtyTotal,
        ...rows,
      };

      doc.setData(data);
      doc.render();

      const filledBuffer = doc.getZip().generate({ type: 'arraybuffer' });

      const container = document.createElement('div');
      container.style.position = 'fixed';
      container.style.left = '0';
      container.style.top = '0';
      container.style.width = '210mm';   // A4
      container.style.maxWidth = '210mm';
      container.style.background = '#ffffff';
      container.style.opacity = '0';
      container.style.pointerEvents = 'none';
      container.style.zIndex = '-1';
      document.body.appendChild(container);

      await renderAsync(filledBuffer, container, null, {
        className: 'docx',
        ignoreWidth: false,
        ignoreHeight: false,
        breakPages: true,
      });

      const target = container.querySelector('.docx') as HTMLElement;
      if (!target) throw new Error('Falha ao renderizar DOCX (elemento .docx não encontrado)');

      if ('fonts' in document) {
        try { await (document as any).fonts.ready; } catch { }
      }
      await waitImages(target);
      await new Promise((r) => requestAnimationFrame(() => r(null))); // 1 frame

      target.style.transform = 'translateX(-29mm)';

      const opt: any = {
        margin: [5, 2, 0, 0],
        filename: `Declaracao_de_Conteudo_${lot?.lotId ?? ''}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff' },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      };

      const worker = html2pdf().set(opt).from(target).toPdf();

      const pdf = await worker.get('pdf');

      if (pdf.internal.getNumberOfPages() > 1) {
        const last = pdf.internal.getNumberOfPages();
        pdf.deletePage(last);
      }

      pdf.save(opt.filename);

      document.body.removeChild(container);
    } catch (err) {
      console.error('Erro ao gerar PDF:', err);
    }
  }
}
